import { API } from 'aws-amplify'
import config from '../config'
import file from './file'
import crypto from './crypto'
import cloneDeep from 'lodash/cloneDeep'

let API_NAME

const PATHS = {
  definitions: '/definitions',
  definition: '/definition',
  definition_application: '/definition_application',
  devices: '/devices',
  devices_serial: '/device',
  measurements: '/measurements',
  measurements_measurement_id: '/measurement',
  measurements_measurement_id_resultsets: '/measurement_resultsets',
  measurements_measurement_id_additional_data: '/measurement_additional_data',
  measurements_average: '/measurements/averaged', // 保留
  memo: '/memo',
  projects: '/projects',
  project: '/project',
  project_judge: '/project_judge',
  auto_judgement_evaluation_index: '/auto_judgement_evaluation_index',
  auto_judgement_project: '/auto_judgement_project',
  make_report: '/make_report',
  magnets: '/magnets',
  magnets_magnet_code: '/magnet',
  error: '/errors',
  post_error: '/noauth/errors',
  users: '/users',
  users_username: '/user',
  calibration: '/calibrations',
  firmwares: '/firmwares',
  firmware: '/firmware',
  release: '/firmware_release',
  sensitivity: '/sensitivities',
  sensitivity_measurement_id: '/sensitivity',
  logs: '/logs',
  log: '/log',
  notice: '/notices',
  aggregate: '/aggregate',
  definition_survey_magnet: '/definition_survey_magnet',
  post_measurement_data: '/post_measurement_data',
  definition_reference: '/definition_reference',
  customers: '/customers',
  orders: '/orders',
  auto_numbering: '/auto_numbering',
  measurements_comparison_additional_data: '/measurement_3',
}

/**
 * 暗号化データ
 * @typedef {Object} CryptoData
 * @property {string} Data 暗号化データ
 * @property {string} Key 暗号化キー保存先のS3キー
 */

/**
 * 暗号化リクエスト
 * @typedef {Object} CryptoRequest
 * @property {object} header
 * @property {CryptoData} body ボディ
 */

/**
 * 要暗号化API用に送信データを暗号化する
 * @param {any} srcData 送信データ
 * @returns {Promise<CryptoRequest>} 暗号化データ
 */
const encrypt = async (request) => {
  // キーの生成（16バイト固定）
  const cryptoKey = crypto.generateKey(16)
  // S3に保存するためのファイル名を生成（スラッシュがあるとフォルダとして区切られてしまうためハイフンに変換）
  const cryptoKeyFileName = crypto.generateKey(16, 'hex')
  const tmpFile = new File([cryptoKey], `${cryptoKeyFileName}.key`, { type: 'text/plain' })
  // S3に保存
  await file.putCryptKey(tmpFile)
  // 生成したキーで暗号化
  const encrypted = crypto.encryptAES(request.body, cryptoKey)

  const encriptedRequest = cloneDeep(request)
  encriptedRequest.body = { Data: encrypted, Key: cryptoKeyFileName }
  return encriptedRequest
}

/**
 * 要暗号化API用に受信データを複合化する
 * @param {CryptoData} response 受信データ
 * @returns {Promise<any>} 複合化データ
 */
const decrypt = async (response) => {
  if (response.Key) {
    const encryptedData = response.Data
    // S3からキーを取得
    const key = await file.getCryptKey(response.Key)
    // 複合化
    return crypto.decryptAES(encryptedData, key)
  }
  // 暗号化キーがレスポンスに存在しない場合はそのまま返す
  return response
}

/**
 * 暗号化キーファイルの削除
 * @param {string[]} keys キーファイル配列（拡張子なし）
 */
const deleteCryptoKey = (keys) => {
  try {
    keys.forEach(key => {
      if (key) file.remove(file.getCryptoKeyPath(key))
    })
  } catch(err) {
    // キーファイルの削除に失敗しても無視する
  }
}

const APIS = {
  getMemo: async (param) => {
    return await API.get(getAPIName(), getPath('memo'),
      toQueryStringParameters(param))
  },
  /*
   param: memo{
    memo_id (新規はなし)
    title
    content
   }
  */
  postMemo: async (param) => {
    return await API.post(getAPIName(), getPath('memo'), param)
  },
  /*
   variable:project_id
  */
  getProject: async (param) => {
    return await API.get(getAPIName(), getPath('project'),
      toQueryStringParameters(param))
  },
  getProjects: async (param) => {
    return await API.get(getAPIName(), getPath('projects'),
      toQueryStringParameters(param))
  },
  getAutoJudgementEvaluationIndex: async (param) => {
    return await API.get(getAPIName(), getPath('auto_judgement_evaluation_index'),
      toQueryStringParameters(param))
  },
  updateAutoJudgementProjectResult: async (param) => {
    return await API.post(getAPIName(), getPath('auto_judgement_project'), param)
  },
  updateProject: async (param) => {
    return await API.post(getAPIName(), getPath('project'), param)
  },
  updateProjectAllResult: async (param) => {
    return await API.post(getAPIName(), getPath('project_judge'), param)
  },
  makeReport: async (param) => {
    return await API.post(getAPIName(), getPath('make_report'), param)
  },
  getMagnets: async (param) => {
    return await API.get(getAPIName(), getPath('magnets'),
      toQueryStringParameters(param))
  },
  getMagnet: async (param) => {
    return await API.get(getAPIName(), getPath('magnets_magnet_code'),
      toQueryStringParameters(param))
  },
  /**
   * body: error:{}, version: string, user:current
   *
   */
  postError: async (param) => {
    return await API.post(getAPIName(), getPath('post_error'), param)
  },
  /**
   * body:
   *  year(2018)
   *  month(01 or 1)
   * }
   * 初期値は今月。
   */
  getErrors: async (param) => {
    return await API.get(getAPIName(), getPath('error'),
      toQueryStringParameters(param))
  },
  getDefinitions: async (param) => {
    return await API.get(getAPIName(), getPath('definitions'),
      toQueryStringParameters(param))
  },
  postDefinition: async (param) => {
    return await API.post(getAPIName(), getPath('definition'), param)
  },
  createApplication: async (param) => {
    return await API.post(getAPIName(), getPath('definition_application'), param)
  },
  getDevices: async (param) => {
    return await API.get(getAPIName(), getPath('devices'),
      toQueryStringParameters(param))
  },
  postDevice: async (param) => {
    return await API.post(getAPIName(), getPath('devices_serial'), param)
  },
  getMeasurements: async (param) => {
    return await API.get(getAPIName(), getPath('measurements'),
      toQueryStringParameters(param))
  },
  getMeasurementsWithId: async (param) => { //保留
    return await API.get(getAPIName(), getPath('measurements_measurement_id'),
      toQueryStringParameters(param))
  },
  createAverage: async (param) => { // 使ってない
    return await API.post(getAPIName(), getPath('measurements_average'), param)
  },
  postAdditionalData: async (param) => {
    return await API.post(getAPIName(), getPath('measurements_measurement_id_additional_data'), param)
  },
  getResultSets: async (param) => {
    return await API.get(getAPIName(), getPath('measurements_measurement_id_resultsets'), toQueryStringParameters(param))
  },
  reanalyze: async (param) => {
    return await API.post(getAPIName(), getPath('measurements_measurement_id_resultsets'), param)
  },
  getMeasurementWithId: async (param) => {
    return await API.get(getAPIName(), getPath('measurements_measurement_id'),
      toQueryStringParameters(param))
  },
  getUserList: async (param) => {
    return await API.get(getAPIName(), getPath('users'),
      toQueryStringParameters(param))
  },
  updateUserAttribute: async (param) => {
    return await API.post(getAPIName(), getPath('users_username'), param)
  },
  createUser: async (param) => {
    return await API.post(getAPIName(), getPath('users'), param)
  },
  getCalibrationList: async (param) => {
    return await API.get(getAPIName(), getPath('calibration'),
      toQueryStringParameters(param))
  },
  getFirmwareList: async (param) => {
    return await API.get(getAPIName(), getPath('firmwares'),
      toQueryStringParameters(param))
  },
  updateFirmware: async (param) => {
    return await API.post(getAPIName(), getPath('firmware'), param)
  },
  release: async (param) => {
    return await API.post(getAPIName(), getPath('release'), param)
  },
  getRelease: async (param) => {
    return await API.get(getAPIName(), getPath('release'),
      toQueryStringParameters(param))
  },
  getSensitivityList: async (param) => {
    return await API.get(getAPIName(), getPath('sensitivity'),
      toQueryStringParameters(param))
  },
  updateSensitivity: async (param) => {
    return await API.post(getAPIName(), getPath('sensitivity_measurement_id'), param)
  },
  getLogList: async (param) => {
    return await API.get(getAPIName(), getPath('logs'),
      toQueryStringParameters(param))
  },
  getLog: async (param) => {
    return await API.get(getAPIName(), getPath('log'),
      toQueryStringParameters(param))
  },
  getNotice: async (param) => {
    return await API.get(getAPIName(), getPath('notice'),
      toQueryStringParameters(param))
  },
  getAggregate: async (param) => {
    return await API.get(getAPIName(), getPath('aggregate'),
      toQueryStringParameters(param))
  },
  updateSurvyMagnet: async (param) => {
    return await API.post(getAPIName(), getPath('definition_survey_magnet'), param)
  },
  upload: async (param) => {
    return await API.post(getAPIName(), getPath('post_measurement_data'), param)
  },
  deleteReference: async (param) => {
    return await API.post(getAPIName(), getPath('definition_reference'), param)
  },
  getCustomers: async (param) => {
    return await API.get(getAPIName(), getPath('customers'),
      toQueryStringParameters(param))
  },
  updateCustomer: async (param) => {
    return await API.post(getAPIName(), getPath('customers'), param)
  },
  getOrders: async (param) => {
    return await API.get(getAPIName(), getPath('orders'),
      toQueryStringParameters(param))
  },
  updateOrder: async (param) => {
    return await API.post(getAPIName(), getPath('orders'), param)
  },
  autoNumbering: async (param) => {
    return await API.get(getAPIName(), getPath('auto_numbering'),
      toQueryStringParameters(param))
  },
  getComparisonMeasurements: async (param) => {
    return await API.get(getAPIName(), getPath('measurements_comparison_additional_data'),
      toQueryStringParameters(param))
  }
}

const toQueryStringParameters = (param) => {
  param.queryStringParameters = param.body
  delete param.body
  return param
}

const getPath = (name) => {
  let path = PATHS[name]
  return path
}

const getAPIName = () => {
  if (!API_NAME) {
    API_NAME = config.getStage()
  }
  return API_NAME
}

/**
 * 要暗号化チェック
 * @param {Function} f APIS内の関数
 */
const isRequiredEncryption = (f) => [
  APIS.createUser,
  APIS.updateUserAttribute,
  APIS.updateCustomer,
  APIS.updateOrder,
].includes(f)

/**
 * 要複合化チェック
 * @param {Function} f APIS内の関数
 */
const isRequiredDecryption = (f) => [
  APIS.getUserList,
  APIS.getCustomers,
  APIS.getOrders,
  APIS.updateOrder,
  APIS.getErrors,
].includes(f)

export default {
  call: async (api, param) => {
    let f = APIS[api]
    if (!f) throw Error('no such API')
    // console.log('calling :' + api + ' with ' + JSON.stringify(param))
    let timeout = setTimeout(() => {
      window.location.href = '/server-error'
    }, 60000)

    let encryptKey = null
    let decryptKey = null

    try {
      if (isRequiredEncryption(f)) {
        param = await encrypt(param)
        encryptKey = param.body.Key
      }

      const response = await f(param)

      if (isRequiredDecryption(f)) {
        decryptKey = response.Key
        return await decrypt(response)
      }
      return response
    } catch (err) {
      if (err.message.indexOf('Network Error') !== -1) {
        window.location.href = '/server-error'
      } else {
        throw Error('Server Error')
      }
    } finally {
      deleteCryptoKey([encryptKey, decryptKey])
      clearTimeout(timeout)
    }
  }
}

